/* eslint-disable react/jsx-filename-extension */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Box,
  Tooltip,
} from '@material-ui/core';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Info as InfoIcon,
  OfflinePin as OfflinePinIcon,
} from '@material-ui/icons';
import moment from 'moment';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Header } from 'commons/components/DataTable/Header';
import { Row } from 'commons/components/DataTable/Row';
import SkeletonList from 'commons/components/SkeletonList';
import CustomPagination from 'commons/components/CustomPagination';
import { useIsMobileDevice } from 'utils/hooks/isMobileDevice';
import { selectState, actionProps } from 'utils/reduxActions';
import { clone, truthty, fNumber } from 'utils/functions';
import { scheduleManagementActions } from '../../reducer';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3),
    overflowX: 'auto',
  },
  container: {
    // maxHeight: 470,
  },
  rowOptions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  greenChip: {
    backgroundColor: theme.palette.primary.light,
    marginRight: '24px',
    maxWidth: '100%',
  },
  blueChip: {
    backgroundColor: theme.palette.info.light,
    marginRight: '8px',
    maxWidth: '100%',
  },
  // Overriding DataTable styles only for this screen to ensure perfect alignment with the header
  '@global': {
    // Body Row
    '.table-row': {
      width: '100% !important',
      display: 'flex !important',
      justifyContent: 'space-between !important',
      height: 'auto !important',
      borderBottom: '1px solid #ececec',
    },
    // Header Wrapper (Box with primary background)
    '.MuiBox-root[style*="background:"]': {
      display: 'flex !important',
      justifyContent: 'space-between !important',
      width: '100% !important',
    },
    // Both Header cells (Typography) and Body cells (.table-row__item)
    '.table-row__item, .MuiBox-root[style*="background:"] > .MuiTypography-root': {
      margin: '0 !important',
      padding: '5px 10px !important',
      display: 'flex !important',
      flexDirection: 'column !important',
      justifyContent: 'center !important',
      alignItems: 'center !important',
      height: '101px !important',
      fontSize: '0.75rem !important',
      textAlign: 'center !important',
      boxSizing: 'border-box !important',
      fontWeight: 'normal !important',
      lineHeight: '1.1 !important',
    },
    // Body cells gap (10px as requested)
    '.table-row .table-row__item': {
      gap: '10px !important',
    },
    // Header cells gap (smaller for multi-line labels)
    '.MuiBox-root[style*="background:"] > .MuiTypography-root': {
      color: 'white !important',
      gap: '2px !important',
    },
    // Remove individual Box/Typography gaps and ensure stacking
    '.table-row__item > .MuiBox-root, .table-row__item > .MuiTypography-root': {
      padding: '0 !important',
      margin: '0 !important',
      textAlign: 'center !important',
      display: 'flex !important',
      flexDirection: 'column !important',
      alignItems: 'center !important',
      justifyContent: 'center !important',
      gap: '10px !important',
    }
  }
}));

function ItemList(props) {
  let schedule_management_read = Object.values(props.objects.roles)[0].name === 'schedule_management';
  let schedule_management_edit = Object.values(props.objects.roles)[0].name === 'schedule_management_edit';

  const { t } = useTranslation();
  const classes = useStyles();
  const isMobileDevice = useIsMobileDevice();
  const [anchorEl, setAnchorEl] = useState(null);

  const {
    loading,
    bookings,
    actions,
    pagination,
    filters,
  } = props;

  const headerList = [
    { id: 'id', name: 'ID', width: '3%' },
    { id: 'producer', name: 'Proveedor', width: '15%' },
    { id: 'processing_site', name: 'Planta', width: '5%' },
    { id: 'carrier', name: 'Transportista', width: '10%' },
    { id: 'date', name: 'Fecha', width: '6%' },
    { 
      id: 'arrival_field_at', 
      name: (
        <>
          <span>Llegada</span>
          <span>Campo</span>
        </>
      ), 
      width: '7%' 
    },
    { 
      id: 'arrival_processing_plant', 
      name: (
        <>
          <span>Llegada</span>
          <span>Planta</span>
        </>
      ), 
      width: '7%' 
    },
    { 
      id: 'reception_variety_truck', 
      name: (
        <>
          <span>Operación/</span>
          <span>Carga/</span>
          <span>Variedad/</span>
          <span>Tipo camión</span>
        </>
      ), 
      width: '18%' 
    },
    { 
      id: 'equivalent_bins', 
      name: (
        <>
          <span>Equivalente</span>
          <span>en Bins</span>
        </>
      ), 
      width: '6%' 
    },
    { 
      id: 'bins', 
      name: (
        <>
          <span>Bins con</span>
          <span>Fruta</span>
        </>
      ), 
      width: '5%' 
    },
    { 
      id: 'r_bins', 
      name: (
        <>
          <span>Bins</span>
          <span>vacíos</span>
        </>
      ), 
      width: '5%' 
    },
    { 
      id: 'returnBins', 
      name: (
        <>
          <span>Retorno</span>
          <span>de Bins</span>
        </>
      ), 
      width: '5%' 
    },
    { 
      id: 'superSack', 
      name: (
        <>
          <span>Maxi</span>
          <span>Sacos</span>
        </>
      ), 
      width: '5%' 
    },
    { id: 'state', name: 'Estado', width: '6%' },
    { id: 'actions', name: "", width: '3%' },
  ];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleDelete = () => {
    handleClose();
    if (anchorEl && anchorEl.dataset.id) {
      actions.toggleDelete(anchorEl.dataset.id);
    }
  };

  const handleChangeRowsPerPage = (e) => {
    actions.paginationControls(filters, {
      ...pagination,
      rowsPerPage: e.target.value,
    });
  };

  const handleChangePage = (e, val) => {
    actions.paginationControls(filters, { ...pagination, page: val });
  };

  const getOptions = (row) => (
    <IconButton onClick={handleClick} data-id={row.id}>
      <MoreVertIcon />
    </IconButton>
  );

  const getChipVariety = (row) => {
    const varietyName = row.Variety?.name;
    const loadCondition = row.loadCondition === 'HUMEDO' ? 'Húmedo' : row.loadCondition === 'SECO' ? 'Seco' : row.loadCondition;
    const truckType = row.truckType ? t(`scheduleManagement.truckTypes.${row.truckType.toLowerCase()}`) : '';
    const operationType = row.operationType === 'PICKUP' ? 'Retiro' : row.operationType === 'DISPATCH' ? 'Despacho' : row.operationType;
    
    return (
      <>
        {operationType && <span>{operationType}</span>}
        {loadCondition && <span>{loadCondition}</span>}
        {varietyName && <span>{varietyName}</span>}
        {truckType && <span>{truckType}</span>}
      </>
    );
  };

  const getChipBins = (qty) => {
      return fNumber && fNumber.format ? fNumber.format(qty) : qty;
  };

  const getChipState = (state) => {
      if (!state) return '-';
      return t(state);
  };

  const getArrayBooking = (row) => [
    { 
      value: (
        <Tooltip title={row.correlative || row.id} placement="top">
          <span>{row.correlative || row.id || '-'}</span>
        </Tooltip>
      )
    },
    { 
      value: (
        <Tooltip title={row.Supplier?.companyName || row.Supplier?.company_name || row.Supplier?.Entity?.name || row.Supplier?.code || '-'} placement="top">
          <span>
            {row.Supplier?.companyName || row.Supplier?.company_name || row.Supplier?.Entity?.name || row.Supplier?.code || '-'}
          </span>
        </Tooltip>
      )
    },
    { 
      value: (
        <Tooltip title={row.ProcessingSite?.name || '-'} placement="top">
          <span>{row.ProcessingSite?.name || '-'}</span>
        </Tooltip>
      )
    },
    { 
      value: (
        <Tooltip title={row.Carrier?.name || '-'} placement="top">
          <span>{row.Carrier?.name || '-'}</span>
        </Tooltip>
      )
    },
    { 
      value: row.scheduledAt ? (
        <>
          <span>{moment(row.scheduledAt).format('DD-MM-YYYY')}</span>
        </>
      ) : '-' 
    },
    { value: row.arrivalFieldAt ? moment(row.arrivalFieldAt).format('HH:mm') : '-' },
    { value: row.arrivalProcessingPlant ? moment(row.arrivalProcessingPlant).format('HH:mm') : '-' },
    { value: getChipVariety(row) },
    { value: ['BATEA', 'AMPLIROLL', 'PISO_CAMINANTE'].includes(row.truckType) ? getChipBins(row.binsQty || 0) : '-' },
    { value: !['BATEA', 'AMPLIROLL', 'PISO_CAMINANTE'].includes(row.truckType) ? getChipBins(row.binsQty || 0) : '-' },
    { value: row.binsEmptyQty === null || row.binsEmptyQty === undefined ? '-' : row.binsEmptyQty }, 
    { value: row.returnBins === null || row.returnBins === undefined ? '-' : row.returnBins },
    { value: row.superSack === null || row.superSack === undefined ? '-' : row.superSack },
    { value: getChipState(row.state) },
    { value: !schedule_management_read && <Box className={classes.rowOptions}>{getOptions(row)}</Box> },
  ];

  if ((!bookings || bookings.length === 0) && !loading) {
    return (
      <Grid item xs={12}>
        <Card className={classes.root}>
          <CardContent>
            <Grid container alignItems="center" justifyContent="center">
              <Typography variant="h6" component="h6">
                {t('scheduleManagement.itemList.noBookings')}
              </Typography>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Card className={classes.root}>
        <CardContent>
          {loading ? (
             <SkeletonList times={pagination?.rowsPerPage || 5} />
          ) : (
            <Grid container spacing={1} className={classes.container}>
              <Grid item xs={12}>
                <Header headerList={headerList} isMobileDevice={isMobileDevice} />
                {truthty(bookings) && bookings.map((booking) => (
                  <Row
                    key={booking.id}
                    row={getArrayBooking(booking)}
                    headerList={headerList}
                    isMobileDevice={isMobileDevice}
                    handleOnClick={() => {}}
                  />
                ))}
              </Grid>
              {pagination && (
                <CustomPagination
                    rowsPerPage={pagination.rowsPerPage}
                    page={pagination.page}
                    count={pagination.count}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    handleChangePage={handleChangePage}
                    loading={loading}
                />
              )}
            </Grid>
          )}

          <Menu anchorEl={anchorEl} keepMounted open={truthty(anchorEl)} onClose={handleClose}>
            <MenuItem onClick={() => { 
              if (anchorEl && anchorEl.dataset.id) {
                const bookingToEdit = bookings.find(b => String(b.id) === String(anchorEl.dataset.id));
                if (bookingToEdit) {
                   actions.toggleEditBooking(bookingToEdit);
                }
              }
              handleClose(); 
            }}>
              <ListItemIcon>
                <EditIcon />
              </ListItemIcon>
              {t('Edit')}
            </MenuItem>
            {!schedule_management_edit && (
            <MenuItem onClick={toggleDelete}>
              <ListItemIcon>
                <DeleteIcon />
              </ListItemIcon>
              {t('Delete')}
            </MenuItem>
            )}
          </Menu>
        </CardContent>
      </Card>
    </Grid>
  );
}

const withConnect = connect(
  selectState(
    'app.objects',
    'scheduleManagement.loading',
    'scheduleManagement.bookings',
    'scheduleManagement.pagination',
    'scheduleManagement.filters',
  ),
  actionProps(
    clone(clone.OBJECT, {
      toggleDelete: scheduleManagementActions.toggleDelete,
      paginationControls: scheduleManagementActions.paginationControls,
      toggleEditBooking: scheduleManagementActions.toggleEditBooking,
    }),
  ),
);

export default compose(withRouter, withConnect)(ItemList);
